import { createTheme, ThemeProvider } from '@mui/material/styles';
import { makeStyles } from '@mui/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Avatar,
    Box,
    Button,
    Chip,
    Grid,
    Tooltip,
    Typography,
} from '@mui/material';
import Alert from '@mui/material/Alert';
import CssBaseline from '@mui/material/CssBaseline';
import Snackbar from '@mui/material/Snackbar';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { DataGridPremium } from '@mui/x-data-grid-premium';
import _ from 'lodash';
import { Fragment, useEffect, useState } from 'react';
import { emitCustomEvent, useCustomEventListener } from 'react-custom-events';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import ColorConstants from '../../defaults/ColorConstants';
import Events from '../../defaults/Events';
import FieldNames from '../../defaults/FieldNames.json';
import GridDefinitions from '../../defaults/GridDefinitions.json';
import {
    genericActualsActivityValidationFieldSetFalse,
    genericActualsActivityValidationFieldSetTrue,
    resetActualsActivityValidationState,
} from '../../redux/slices/actualsActivityValidationSlice';
import {
    genericActualsTreatmentValidationFieldSetFalse,
    genericActualsTreatmentValidationFieldSetTrue,
    resetActualsTreatmentValidationState,
} from '../../redux/slices/actualsTreatmentValidationSlice';
import AuditLogService from '../../services/AuditLogService';
import CancelService from '../../services/CancelService';
import ErrorService from '../../services/ErrorService';
import FeatureService from '../../services/FeatureService';
import FundingService from '../../services/FundingService';
import GeometryService from '../../services/GeometryService';
import GroupingService from '../../services/GroupingService';
import IDService from '../../services/IDService';
import LookupService from '../../services/LookupService';
import PlannedActualsService from '../../services/PlannedActualsService';
import { getLogForFieldUpdatesOnExistingFeature } from '../../utils/AuditLogHelper';
import { getByLevel, getPostfireValueReference } from '../../utils/CategoryAssociationHelper';
import {
    calculateFiscalYearQuarter,
    compareLocalDatesFromIsoString,
    formatDateForDisplay,
    getSaveableDate,
} from '../../utils/DateHelper';
import {
    addMeasurementEntry,
    getAllMissingFields,
    getAllMissingFieldsCount,
    getDepartureIndexValueForLabel,
    getDepartureOptions,
    getFormGroupings,
    getRequiredFields,
    getStatus,
    isFeaturePostFire,
} from '../../utils/FeatureHelper';
import InFormAlert from '../InFormAlert';
import '../list/TreatmentActivityList/TreatmentActivityList.css';
import './DetailsContainer.css';
import DetailsEdited from './DetailsEdited';
import DetailsHeader from './DetailsHeader';
import DetailsRequired from './DetailsRequired';
import fieldsDefinition from './FieldInfo.json';
import FormGroupings from './FormGroupings.json';
import AssociateChips from './customPanels/AssociateChips';
import CostsFundingPopover from './customPanels/CostsFundingPopover';
import DetailsActualsGridItem from './customPanels/DetailsActualsGridItem';
import DetailsPlannedGridItem from './customPanels/DetailsPlannedGridItem';
import DetailsPopover from './customPanels/DetailsPopover';
import * as esriLang from '@arcgis/core/core/lang.js';
import InteragencyService from '../../services/InteragencyService';
import ProjectService from '../../services/ProjectService';
import AddIcon from '@mui/icons-material/Add';
import ValuesPanel from './ValuesPanel/ValuesPanel';
import { setShowGlobalLoader, setStatusOptions } from '@store/slices/globalContextSlice';
import {
    setFeature,
    setIsWithinFiscalYear,
    setIsCompleted,
    setMode,
    setIsActual,
    updatePlannedFeatureAttributes,
    setPostFireValueReference,
} from '../../redux/slices/detailsSlice';
import {setIsNewFeature, setListCheckedFeatures} from '../../redux/slices/displayStateSlice';
import { createStyledDrawer } from '../shared/StyledDrawer';
import {
    calculateEditableFiscalYearOffsetByDay, findOcc,
    handleEstablishGridColumns,
    handleExpandedChanged, rederiveFeatureInfo
} from "../shared/DetailsSharedLogic";
import {selectPlannedFeature} from "../../utils/ReduxHelper";

const Drawer = createStyledDrawer(715);

const theme = createTheme({
    overrides: {
        MuiSnackbar: {
            root: {
                top: '94px !important',
            },
        },
    },
});

const useStyles = makeStyles(() => ({
    snackBarStyle: {
        top: '120px !important',
    },
}));

async function getActualRecords(joinID, joinFeature) {
    const whereClause = `(${FieldNames.estimatedActuals.joinIDE} = ${joinID}) AND (${FieldNames.estimatedActuals.joinFeatureE} = '${joinFeature}') AND (IsArchived = 'No' OR IsArchived IS NULL)`;
    return await PlannedActualsService.getAllPlannedActualRecords(whereClause);
}

//TODO: Break out Snackbar into own control, make more obvious.

export default function DetailsActualsContainer({
    plannedActualFeature,
    plannedGroups,
    plannedFunding,
    plannedInteragencySupport,
    geometryType,
}) {
    const navigate = useNavigate();

    const dispatch = useDispatch();
    const userInfo = useSelector((state) => state.globalContext.userInfo);
    const mode = useSelector((state) => state.details.mode);
    const isProject = useSelector((state) => state.details.isProject);
    const plannedFeature = useSelector(selectPlannedFeature)


    const config = useSelector((state) => state.configuration.config);
    const rootServiceUrl = useSelector((state) => state.configuration.rootServiceUrl);
    const treatmentValidationUnsavedValues = useSelector((state) => {
        return state.actualsTreatmentValidation;
    });
    const activityValidationUnsavedValues = useSelector((state) => {
        return state.actualsActivityValidation;
    });
    const statusOptions = useSelector((state) => state.globalContext.statusOptions);
    const overrides = useSelector((state) => state.globalOverrides);
    const isNewFeature = useSelector((state) => state.displayState.isNewFeature);
    const feature = useSelector((state) => state.details.feature);
    const isTreatment = useSelector((state) => state.details.isTreatment);

    const classes = useStyles();

    const departureIndexOptions = getDepartureOptions();

    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [associatedCount, setAssociatedCount] = useState(0);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('success');
    const [groupDetails, setGroupDetails] = useState();
    // Action and related state constants
    const [editableFeature, setEditableFeature] = useState(null);
    const [disableEdit, setDisableEdit] = useState(false);
    const [disableEditMessage, setDisableEditMessage] = useState(false);
    const [baseFeatureAttributes, setBaseFeatureAttributes] = useState(null);
    const [plannedEditableFeature, setPlannedEditableFeature] = useState(null);
    const [expandedItems, setExpandedItems] = useState([]);
    const [programValue, setProgramValue] = useState('');
    const [classValue, setClassValue] = useState('');
    const [categoryValue, setCategoryValue] = useState('');
    const [typeValue, setTypeValue] = useState('');
    const [subTypeValue, setSubTypeValue] = useState('');
    const [unitOfMeasureValue, setUnitOfMeasureValue] = useState('');
    const [isCancelled, setIsCancelled] = useState(false);
    const [dateFYWarningOpen, setDateFYWarningOpen] = useState(false);
    const [dateFYWarningVal, setDateFYWarningVal] = useState('');
    const [locationAgencyValue, setLocationAgencyValue] = useState('');
    const [locationRegionValue, setLocationRegionValue] = useState('');
    const [locationUnitValue, setLocationUnitValue] = useState('');
    const [tribeNameValue, setTribeNameValue] = useState('');
    const [featureHistoryRows, setFeatureHistoryRows] = useState([]);
    const [featureHistoryCols, setFeatureHistoryCols] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaveDisabled, setIsSaveDisabled] = useState(false);
    const [featureGroups, setFeatureGroups] = useState([]);
    const [doClearCancelLogic, setDoClearCancelLogic] = useState(false);
    const [costFundingPopoverOpen, setCostFundingPopoverOpen] = useState(false);
    const [costFundingPopoverUsePlannedActual, setCostFundingPopoverUsePlannedActual] =
        useState(false);
    const [hasPostFireValues, setHasPostFireValues] = useState(false);
    const [interagencySelected, setInteragencySelected] = useState([]);
    const [otherInteragencySelected, setOtherInteragencySelected] = useState([]);
    const [valueReference, setValueReference] = useState([]);

    const completionDate =
        editableFeature?.attributes?.[FieldNames.featureFields.completionDate] ??
        editableFeature?.attributes?.[FieldNames.featureFields.completionDate] ??
        null;

    let requiredFields = isTreatment
        ? FieldNames.actualTreatmentRequiredFields
        : FieldNames.actualActivityRequiredFields;

    useCustomEventListener(Events.TriggerFieldChange, (data) => {
        if (data) {
            onHandleAttributeChanged(data.field, data.value);
        }
    });

    useEffect(() => {
        dispatch(setIsActual(true));
        handleEstablishGridColumns(setFeatureHistoryCols);
    }, []);

    useEffect(() => {
        if (feature) {
            const isPostFire = isFeaturePostFire(feature?.attributes, isProject);
            if (isPostFire && feature?.attributes) {
                getPostfireValueReference(
                    feature,
                    config.postfire[config.env].postfireValueReference.layerId
                ).then((resp) => {
                    setValueReference(resp);
                });
            }
            const tempFormGroupings = getFormGroupings(isTreatment, feature);
            setGroupDetails(tempFormGroupings);
            let tempInteragencySelected = [];
            let tempOtherInteragencySelected = [];

            feature.attributes[FieldNames.featureFields.interagencyName]
                ?.split('|')
                ?.forEach((interagency) => {
                    if (
                        interagency !== FieldNames.interagencyValues.other &&
                        interagency?.includes(FieldNames.interagencyValues.otherShortened)
                    ) {
                        tempOtherInteragencySelected.push({
                            [FieldNames.interagencyFields.interagencyName]: interagency,
                        });
                    } else {
                        tempInteragencySelected.push({
                            [FieldNames.interagencyFields.interagencyName]: interagency,
                        });
                    }
                });

            setInteragencySelected(tempInteragencySelected);
            setOtherInteragencySelected(tempOtherInteragencySelected);
        }
    }, [feature, isTreatment]);


    useEffect(() => {
        if (rootServiceUrl && config) {
            if (!statusOptions || (statusOptions && statusOptions.length === 0)) {
                LookupService.getStatuses(false)
                    .then((response) => {
                        if (response && response.length > 0) {
                            const statusNames = response.map(
                                (x) => x[FieldNames.statusTable.status]
                            );
                            dispatch(setStatusOptions(statusNames));
                        }
                    })
                    .catch((error) => {
                        ErrorService.addErrorLogEntry(
                            error
                        );
                    });
            }
        }
    }, [rootServiceUrl, config, statusOptions, setStatusOptions]);

    useEffect(() => {
        // Note: If we update this in the future, determine if the disableDelete parameter passed to DetailsHeader needs to be separated from DisableEdit
        if (completionDate) {
            const formattedDate = getSaveableDate(completionDate);
            let featureFiscalYear = calculateFiscalYearQuarter(formattedDate, true);
            if (featureFiscalYear?.toString().length === 4) {
                let currentEditableFiscalYear = calculateEditableFiscalYearOffsetByDay(new Date());
                const override = getActiveOverrideDate(isFeaturePostFire(editableFeature));
                let isInPreviousFY = override
                    ? !override
                    : featureFiscalYear < currentEditableFiscalYear;

                if (isInPreviousFY) {
                    setDisableEditMessage(
                        'Edit is disabled for this action as we are past the end of the fiscal year and data is now locked'
                    );
                    dispatch(setMode(FieldNames.modes.read));
                    setDisableEdit(true);
                }
            }
        }
    }, [
        mode,
        completionDate
    ]);

    const getActiveOverrideDate = (isPostFire = false) => {
        if (overrides) {
            if (isPostFire) {
                if (overrides.postFire && overrides.postFire.fiscalYearOverridesActive) {
                    return compareLocalDatesFromIsoString(
                        overrides.postFire.fiscalYearOverridesDate,
                        new Date()
                    );
                } else {
                    return false;
                }
            } else {
                if (overrides.fuels && overrides.fuels.fiscalYearOverridesActive) {
                    return compareLocalDatesFromIsoString(
                        overrides.fuels.fiscalYearOverridesDate,
                        new Date()
                    );
                } else {
                    return false;
                }
            }
        } else {
            return false;
        }
    };

    useEffect(() => {
        //Build treatment here?
        let tempFeature = esriLang.clone(feature);
        let tempPlannedFeature = esriLang.clone(plannedFeature);
        if (
            tempFeature?.attributes &&
            tempPlannedFeature?.attributes &&
            tempFeature.attributes[FieldNames.featureFields.id] !==
                tempPlannedFeature.attributes[FieldNames.featureFields.id]
        ) {
            setProgramValue(tempFeature.attributes[FieldNames.featureFields.program]);
            setClassValue(tempFeature.attributes[FieldNames.featureFields.class]);
            setCategoryValue(tempFeature.attributes[FieldNames.featureFields.category]);
            setTypeValue(tempFeature.attributes[FieldNames.featureFields.type]);
            if (
                tempFeature.attributes[FieldNames.featureFields.status] !==
                FieldNames.statuses.cancelled
            ) {
                if (
                    mode === FieldNames.modes.edit &&
                    (isNewFeature ||
                        (!tempFeature.attributes[FieldNames.featureFields.status] &&
                            !tempPlannedFeature.attributes[FieldNames.featureFields.status]))
                ) {
                    const initialStatus = getStatus(tempFeature, false, true, 0);
                    tempFeature.attributes[FieldNames.featureFields.status] = initialStatus;
                    tempPlannedFeature.attributes[FieldNames.featureFields.status] = initialStatus;
                }
            }
        }
        setEditableFeature(tempFeature);
        isNewFeature && setPlannedEditableFeature(tempPlannedFeature);
    }, [feature, mode, isNewFeature]);

    useEffect(() => {
        if (editableFeature && editableFeature.attributes) {
            if (baseFeatureAttributes === null) {
                setBaseFeatureAttributes(_.cloneDeep(editableFeature.attributes));
            }
            if (
                editableFeature.attributes[FieldNames.featureFields.class] ===
                FieldNames.classTypes.actualTreatment
            ) {
                dispatch(resetActualsTreatmentValidationState());
            }
            if (
                editableFeature.attributes[FieldNames.featureFields.class] ===
                FieldNames.classTypes.actualActivity
            ) {
                dispatch(resetActualsActivityValidationState());
            }

            if (!featureHistoryRows || featureHistoryRows.length === 0) {
                getFeatureAuditLog(
                    editableFeature.attributes[FieldNames.featureFields.joinID],
                    editableFeature.attributes[FieldNames.featureFields.joinFeature]
                );
            }

            setTribeNameValue(
                editableFeature.attributes[FieldNames.featureFields.tribeName]
                    ? editableFeature.attributes[FieldNames.featureFields.tribeName]
                    : ''
            );

            let isComplete = editableFeature.attributes[FieldNames.featureFields.completionDate]
                ? true
                : false;
            dispatch(setIsCompleted(isComplete));
            // Validate FY dates
            if (editableFeature.attributes[FieldNames.featureFields.initiationDate]) {
                let featureFiscalYear = calculateFiscalYearQuarter(
                    editableFeature.attributes[FieldNames.featureFields.initiationDate],
                    true
                );
                if (featureFiscalYear && featureFiscalYear.toString().length === 4) {
                    //new Date starts at 0 instead of 1 so October is really 09
                    let currentEditableFiscalYear = calculateEditableFiscalYearOffsetByDay(
                        new Date()
                    );
                    const override = getActiveOverrideDate(isFeaturePostFire(editableFeature));
                    let isCurrentFiscalYear = override
                        ? override
                        : featureFiscalYear
                          ? currentEditableFiscalYear - 1 <= featureFiscalYear
                          : true;
                    if (!isCurrentFiscalYear) {
                        setDateFYWarningVal(FieldNames.featureFields.initiationDate);
                        setDateFYWarningOpen(true);
                    }
                    dispatch(setIsWithinFiscalYear(isCurrentFiscalYear));
                }
            }
            if (editableFeature.attributes[FieldNames.featureFields.completionDate]) {
                let featureFiscalYear = calculateFiscalYearQuarter(
                    editableFeature.attributes[FieldNames.featureFields.completionDate],
                    true
                );
                if (featureFiscalYear && featureFiscalYear.toString().length === 4) {
                    //new Date starts at 0 instead of 1 so October is really 09
                    let currentEditableFiscalYear = calculateEditableFiscalYearOffsetByDay(
                        new Date()
                    );
                    const override = getActiveOverrideDate(isFeaturePostFire(editableFeature));
                    let isCurrentFiscalYear = override
                        ? override
                        : featureFiscalYear
                          ? currentEditableFiscalYear <= featureFiscalYear
                          : true;
                    if (!isCurrentFiscalYear) {
                        setDateFYWarningVal(FieldNames.featureFields.completionDate);
                        setDateFYWarningOpen(true);
                    }
                    dispatch(setIsWithinFiscalYear(isCurrentFiscalYear));
                }
            }
            if (editableFeature.attributes[FieldNames.featureFields.cancelledDate]) {
                let featureFiscalYear = calculateFiscalYearQuarter(
                    editableFeature.attributes[FieldNames.featureFields.cancelledDate],
                    true
                );
                if (featureFiscalYear && featureFiscalYear.toString().length === 4) {
                    //new Date starts at 0 instead of 1 so October is really 09
                    let currentEditableFiscalYear = calculateEditableFiscalYearOffsetByDay(
                        new Date()
                    );
                    const override = getActiveOverrideDate(isFeaturePostFire(editableFeature));
                    let isCurrentFiscalYear = override
                        ? override
                        : featureFiscalYear
                          ? currentEditableFiscalYear <= featureFiscalYear
                          : true;
                    if (!isCurrentFiscalYear) {
                        setDateFYWarningVal(FieldNames.featureFields.cancelledDate);
                        setDateFYWarningOpen(true);
                    }
                    dispatch(setIsWithinFiscalYear(isCurrentFiscalYear));
                }
            }
        }
    }, [editableFeature, setIsCompleted]);

    const haveFeatureValuesChanged = () => {
        if (baseFeatureAttributes === null || editableFeature === null) {
            return false;
        }
        for (var prop in editableFeature.attributes) {
            var editableProp = editableFeature.attributes[prop];
            var baseProp = baseFeatureAttributes[prop];
            if (
                (baseProp !== null && baseProp !== editableProp) ||
                (baseProp === null && editableProp)
            ) {
                if (isTreatment) {
                    dispatch(genericActualsTreatmentValidationFieldSetTrue(prop));
                } else {
                    dispatch(genericActualsActivityValidationFieldSetTrue(prop));
                }
            } else {
                if (isTreatment) {
                    dispatch(genericActualsTreatmentValidationFieldSetFalse(prop));
                } else {
                    dispatch(genericActualsActivityValidationFieldSetFalse(prop));
                }
            }
        }
    };

    useEffect(() => {
        if (baseFeatureAttributes === null || editableFeature === null) {
            return;
        }
        haveFeatureValuesChanged();
    }, [baseFeatureAttributes, editableFeature]);

    useEffect(() => {
        let obj = {};
        const allIds = groupDetails
            ? groupDetails.map((element) => {
                  return element.identifier;
              })
            : [];

        allIds.forEach((id) => {
            obj[id] = 'true';
        });
        setExpandedItems(obj);
    }, [groupDetails, isTreatment]);

    const getFeatureAuditLog = (joinID, joinFeature) => {
        if (joinID) {
            AuditLogService.getEntityAuditLog(joinFeature, joinID)
                .then((auditRows) => {
                    if (auditRows && auditRows.length > 0) {
                        const mappedRows = auditRows.map((auditRow) => {
                            try {
                                return {
                                    id: auditRow.OBJECTID,
                                    ModifiedBy: auditRow.ModifiedBy,
                                    ModifiedDate: formatDateForDisplay(auditRow.ModifiedDate),
                                    Modification: JSON.parse(auditRow.AuditEntryJson)[0],
                                };
                            } catch (e) {
                                return {
                                    id: auditRow.OBJECTID,
                                    ModifiedBy: auditRow.ModifiedBy,
                                    ModifiedDate: formatDateForDisplay(auditRow.ModifiedDate),
                                    Modification: auditRow.AuditEntryJson,
                                };
                            }
                        });
                        setFeatureHistoryRows(mappedRows);
                    } else {
                        setFeatureHistoryRows([]);
                    }
                })
                .catch((error) => {
                    ErrorService.addErrorLogEntry(
                        error
                    );
                });
        }
    };

    const addFeatureAuditLogEntry = (feature, featureID, joinFeature, isNew, reloadGrid) => {
        const changedFields = isTreatment
            ? treatmentValidationUnsavedValues
            : activityValidationUnsavedValues;
        const changeString = getLogForFieldUpdatesOnExistingFeature(changedFields);
        return new Promise(function (resolve) {
            AuditLogService.addAuditLogEntry(
                feature,
                featureID,
                joinFeature,
                userInfo[FieldNames.userFields.nifcUsername],
                [isNew ? 'Feature was created' : changeString]
            )
                .then((response) => {
                    if (!isNew && reloadGrid) {
                        getFeatureAuditLog(featureID, joinFeature);
                    }
                    resolve('OK');
                })
                .catch((error) => {
                    ErrorService.addErrorLogEntry(
                        error
                    );
                });
        });
    };

    const rederiveFeatureCallback = (
        feature,
        isTreatment,
        isFeatureActuals,
        isPoint,
        wfftsAttributes,
        legacyAttributes
    ) => {
        const resetAcreage = false;
        rederiveFeatureInfo(
            feature,
            isTreatment,
            isFeatureActuals,
            isPoint,
            wfftsAttributes,
            legacyAttributes,
            resetAcreage
        );
    };

    const handleClose = () => {
        setDateFYWarningOpen(false);
    };

    const handleContinue = () => {
        setDateFYWarningOpen(false);
        onHandleDateWithFYFQChanged(dateFYWarningVal, null);
        if (dateFYWarningVal === FieldNames.featureFields.completionDate) {
            dispatch(setIsWithinFiscalYear(true));
        }
    };

    const title = (detail) => {
        return (
            <div className='incident-details-panel-header'>
                <h4>{detail.title}</h4>
            </div>
        );
    };

    const onHandleDateWithFYFQChanged = (attributeName, attributeValue) => {
        let updatedProp = {};
        let year = calculateFiscalYearQuarter(attributeValue, true);
        let quarter = calculateFiscalYearQuarter(attributeValue, false);
        if (attributeName === FieldNames.featureFields.completionDate) {
            let initValue = null;
            if (attributeValue && typeof attributeValue === 'object') {
                initValue = attributeValue.getTime();
            }
            updatedProp[attributeName] = initValue;
            updatedProp[FieldNames.featureFields.completionFiscalQuarter] = quarter;
            updatedProp[FieldNames.featureFields.completionFiscalYear] = year;
        } else if (attributeName === FieldNames.featureFields.initiationDate) {
            let initValue = null;
            if (attributeValue && typeof attributeValue === 'object') {
                initValue = attributeValue.getTime();
            }
            updatedProp[attributeName] = initValue;
            updatedProp[FieldNames.featureFields.initiationFiscalQuarter] = quarter;
            updatedProp[FieldNames.featureFields.initiationFiscalYear] = year;
        } else if (attributeName === FieldNames.featureFields.cancelledDate) {
            let initValue = attributeValue;
            if (attributeValue && typeof attributeValue === 'object') {
                initValue = attributeValue.getTime();
            } else {
                initValue = null;
            }

            if (
                attributeName === FieldNames.featureFields.cancelledDate &&
                plannedFeature?.attributes
            ) {
                let removingCancelledDate = false;
                if (!plannedFeature.attributes[attributeName] && initValue) {
                    updatedProp[FieldNames.featureFields.isCanceled] = FieldNames.yesNo.yes;
                } else if (plannedFeature.attributes[attributeName] && !initValue) {
                    updatedProp[FieldNames.featureFields.isCanceled] = FieldNames.yesNo.no;
                    removingCancelledDate = true;
                }
                setDoClearCancelLogic(removingCancelledDate);
            }

            updatedProp[attributeName] = initValue;
            if (isTreatment) {
                dispatch(updatePlannedFeatureAttributes({ [attributeName]: initValue}))
            } else {
                dispatch(updatePlannedFeatureAttributes({ [attributeName]: initValue}))
            }
        }
        var editedFields = {
            attributes: Object.assign({}, editableFeature.attributes, updatedProp),
        };
        const updatedStatus = getStatus(editedFields, false, true, 0);
        editableFeature.attributes[FieldNames.featureFields.status] = updatedStatus;
        if (
            plannedFeature.attributes[FieldNames.featureFields.isProgressiveReporting] !==
            FieldNames.yesNo.yes
        ) {
            dispatch(updatePlannedFeatureAttributes({ [FieldNames.featureFields.status]: updatedStatus}))

        }
        const updatedAttributes = {
            attributes: Object.assign({}, editableFeature.attributes, updatedProp),
        };
        const updatedFeature = Object.assign({}, editableFeature, updatedAttributes);
        setEditableFeature(updatedFeature);
    };

    const onHandleAttributeChanged = (attributeName, attributeValue) => {
        let updatedProp = {};

        if (attributeName === FieldNames.featureFields.cancelledDate) {
            let initValue = attributeValue;
            if (attributeValue && typeof attributeValue === 'object') {
                initValue = attributeValue.getTime();
            } else {
                initValue = null;
            }
            if (
                attributeName === FieldNames.featureFields.cancelledDate &&
                plannedFeature &&
                plannedFeature.attributes
            ) {
                let removingCancelledDate = false;
                if (!plannedFeature.attributes[attributeName] && initValue) {
                    updatedProp[FieldNames.featureFields.isCanceled] = FieldNames.yesNo.yes;
                } else if (plannedFeature.attributes[attributeName] && !initValue) {
                    updatedProp[FieldNames.featureFields.isCanceled] = FieldNames.yesNo.no;
                    removingCancelledDate = true;
                }
                setDoClearCancelLogic(removingCancelledDate);
            }
            updatedProp[attributeName] = initValue;
            dispatch(updatePlannedFeatureAttributes({ [attributeName]: initValue}))

        } else {
            updatedProp[attributeName] = attributeValue !== '' ? attributeValue : null;

            if (attributeName === FieldNames.featureFields.category) {
                if (!isFeaturePostFire(plannedEditableFeature)) {
                    updatedProp[FieldNames.featureFields.type] = '';
                    updatedProp[FieldNames.featureFields.subType] = '';
                }

                updatedProp[FieldNames.featureFields.measureAmount] = null;
                updatedProp[FieldNames.featureFields.unitOfMeasure] = '';
            }

            if (attributeName === FieldNames.featureFields.type) {
                if (!isFeaturePostFire(plannedEditableFeature)) {
                    updatedProp[FieldNames.featureFields.subType] = '';
                }
                updatedProp[FieldNames.featureFields.measureAmount] = null;
                updatedProp[FieldNames.featureFields.unitOfMeasure] = '';
            }

            if (attributeName === FieldNames.featureFields.class) {
                //reset every time class changes
                updatedProp[FieldNames.featureFields.category] = '';
                updatedProp[FieldNames.featureFields.type] = '';
                updatedProp[FieldNames.featureFields.subType] = '';
                updatedProp[FieldNames.featureFields.measureAmount] = null;
                updatedProp[FieldNames.featureFields.unitOfMeasure] = '';
            }

        }
        if (editableFeature?.attributes) {
            if (attributeName === FieldNames.featureFields.vegDeparturePercentageManual) {
                const saveableValue = getDepartureIndexValueForLabel(attributeValue);
                updatedProp[FieldNames.featureFields.vegDeparturePercentageManual] = saveableValue;
                updatedProp[FieldNames.featureFields.isVegetationManual] = FieldNames.yesNo.yes;
            }

            if (
                attributeName === FieldNames.featureFields.statusReason &&
                editableFeature.attributes[FieldNames.featureFields.status] ===
                    FieldNames.statuses.cancelled
            ) {
                if (
                    plannedFeature.attributes[FieldNames.featureFields.isProgressiveReporting] !==
                    FieldNames.yesNo.yes
                ) {
                    dispatch(updatePlannedFeatureAttributes({ [FieldNames.featureFields.statusReason]: attributeValue}))
                }
            }

            if (userInfo) {
                updatedProp[FieldNames.featureFields.modifiedBy] =
                    userInfo[FieldNames.userFields.nifcUsername];
            }

            if (updatedProp) {
                const catProp = updatedProp.hasOwnProperty(FieldNames.featureFields.category);
                const fundUnitTypeProp = updatedProp.hasOwnProperty(
                    FieldNames.featureFields.fundingUnitType
                );
                if ((catProp || fundUnitTypeProp) && !isTreatment) {
                    // FUELS-504: Change was made to category and/or fundingUnitType, check for point management activity
                    checkPointManagementActivityChange(
                        editableFeature,
                        catProp
                            ? updatedProp[FieldNames.featureFields.category]
                                ? updatedProp[FieldNames.featureFields.category]
                                : ''
                            : editableFeature.attributes[FieldNames.featureFields.category],
                        fundUnitTypeProp
                            ? updatedProp[FieldNames.featureFields.fundingUnitType]
                                ? updatedProp[FieldNames.featureFields.fundingUnitType]
                                : ''
                            : editableFeature.attributes[FieldNames.featureFields.fundingUnitType]
                    );
                }
            }

            const updatedAttributes = {
                attributes: Object.assign({}, editableFeature.attributes, updatedProp),
            };
            const updatedFeature = Object.assign({}, editableFeature, updatedAttributes);
            setEditableFeature(updatedFeature);
            //Set the status

            if (attributeName === FieldNames.featureFields.status) {
                if (attributeValue !== FieldNames.statuses.cancelled) {
                    setIsCancelled(false);
                } else {
                    setIsCancelled(true);
                    editableFeature.attributes[FieldNames.featureFields.cancelledDate] =
                        new Date().getTime();
                    dispatch(updatePlannedFeatureAttributes({ [FieldNames.featureFields.cancelledDate]: new Date().getTime()}))
                    dispatch(updatePlannedFeatureAttributes({ [FieldNames.featureFields.status]: FieldNames.statuses.cancelled}))
                }
            } else {
                let filterFields = null;
                if (attributeName === FieldNames.featureFields.cancelledDate && !attributeValue) {
                    filterFields = [FieldNames.featureFields.statusReason];
                }
                const updatedFields = {
                    attributes: Object.assign({}, editableFeature.attributes, updatedProp),
                };
                const missingFieldsCount = getAllMissingFieldsCount(
                    updatedFields,
                    isTreatment,
                    true,
                    filterFields
                );
                let statusToSet = getStatus(updatedFields, false, true, missingFieldsCount);
                updatedProp[FieldNames.featureFields.status] = statusToSet;
                if (
                    plannedFeature.attributes[FieldNames.featureFields.isProgressiveReporting] !==
                    FieldNames.yesNo.yes
                ) {
                    dispatch(updatePlannedFeatureAttributes({ [FieldNames.featureFields.status]: statusToSet}))
                }
            }
        }
    };

    const checkPointManagementActivityChange = (feature, newCategory, newUnitType) => {
        if (
            feature &&
            feature.attributes &&
            feature.attributes[FieldNames.featureFields.isPoint] === 'Yes' &&
            !isTreatment
        ) {
            if (
                (feature.attributes[FieldNames.featureFields.fundingUnitType] ===
                    FieldNames.unitTypes.national ||
                    feature.attributes[FieldNames.featureFields.fundingUnitType] ===
                        FieldNames.unitTypes.regional) &&
                categoryValue === FieldNames.categoriesForChecking.programManagement &&
                (!(
                    newUnitType === FieldNames.unitTypes.national ||
                    newUnitType === FieldNames.unitTypes.regional
                ) ||
                    newCategory !== FieldNames.categoriesForChecking.programManagement)
            ) {
                // Feature is being altered from the conditions. Feature is always a point per original FUELS-504 conditions
                rederiveFeatureCallback(feature, isTreatment, true, true, null, null);
            } else if (
                (!(
                    feature.attributes[FieldNames.featureFields.fundingUnitType] ===
                        FieldNames.unitTypes.national ||
                    feature.attributes[FieldNames.featureFields.fundingUnitType] ===
                        FieldNames.unitTypes.regional
                ) ||
                    categoryValue !== FieldNames.categoriesForChecking.programManagement) &&
                (newUnitType === FieldNames.unitTypes.national ||
                    newUnitType === FieldNames.unitTypes.regional) &&
                newCategory === FieldNames.categoriesForChecking.programManagement
            ) {
                // Feature is being altered to the conditions. Set the location fields to the funding fields.
                setLocationFieldsToFundingFields(feature);
                feature.attributes[FieldNames.featureFields.locationDepartmentLocked] =
                    FieldNames.yesNo.no;
                feature.attributes[FieldNames.featureFields.locationAgencyLocked] =
                    FieldNames.yesNo.no;
                feature.attributes[FieldNames.featureFields.locationRegionLocked] =
                    FieldNames.yesNo.no;
                feature.attributes[FieldNames.featureFields.locationUnitLocked] =
                    FieldNames.yesNo.no;
            }
        }
    };

    const setLocationFieldsToFundingFields = (feature) => {
        // Check if feature is activity/point, program management category, and unit type of national/regional
        if (feature && feature.attributes) {
            var fundingDepVal = feature.attributes[FieldNames.featureFields.fundingDepartment]
                ? feature.attributes[FieldNames.featureFields.fundingDepartment].includes('|')
                    ? 'NA Multiple Funding Units'
                    : feature.attributes[FieldNames.featureFields.fundingDepartment]
                : '';
            var fundingAgeVal = feature.attributes[FieldNames.featureFields.fundingAgency]
                ? feature.attributes[FieldNames.featureFields.fundingAgency].includes('|')
                    ? 'NA Multiple Funding Units'
                    : feature.attributes[FieldNames.featureFields.fundingAgency]
                : '';
            var fundingRegVal = feature.attributes[FieldNames.featureFields.fundingRegion]
                ? feature.attributes[FieldNames.featureFields.fundingRegion].includes('|')
                    ? 'NA Multiple Funding Units'
                    : feature.attributes[FieldNames.featureFields.fundingRegion]
                : '';
            var fundingUnitVal = feature.attributes[FieldNames.featureFields.fundingUnit]
                ? feature.attributes[FieldNames.featureFields.fundingUnit].includes('|')
                    ? 'NA Multiple Funding Units'
                    : feature.attributes[FieldNames.featureFields.fundingUnit]
                : '';

            feature.attributes[FieldNames.featureFields.department] = fundingDepVal;
            feature.attributes[FieldNames.featureFields.agency] = fundingAgeVal;
            setLocationAgencyValue(fundingAgeVal);
            feature.attributes[FieldNames.featureFields.region] = fundingRegVal;
            setLocationRegionValue(fundingRegVal);
            feature.attributes[FieldNames.featureFields.unit] = fundingUnitVal;
            setLocationUnitValue(fundingUnitVal);
            // TODO - URGENT: 'feature' is being mutated directly. Needs to use a setter. Leaving as is for now to avoid breaking changes, but needs to be refactored when possible.
        }
    };

    const onHandleEditClicked = () => {
        dispatch(setMode(FieldNames.modes.edit)); //TODO: Throw into enum or something
    };

    const createGroupItemsForActual = (
        isActualTreatment,
        actualsFeatureResults,
        futureActualFeature,
        actualsIdVal
    ) => {
        if (plannedGroups && plannedGroups.Groups && plannedGroups.Groups.length > 0) {
            let plannedGroupsAll = plannedGroups.Groups;
            let plannedGroupItems = plannedGroups.GroupItems;
            let groupItemsToSave = [];
            plannedGroupsAll.forEach((group) => {
                let allGroupItemsByGroup = [];
                let sequenceNumbers = [];
                let nextSequenceNumber = 1;
                if (plannedGroupItems && plannedGroupItems.length > 0) {
                    allGroupItemsByGroup = plannedGroupItems.filter(
                        (x) =>
                            x[
                                FieldNames.treatmentActivityGroupItems.treatmentActivityGroupJoinID
                            ] === group[FieldNames.treatmentActivityGroups.id]
                    );
                    if (allGroupItemsByGroup && allGroupItemsByGroup.length > 0) {
                        sequenceNumbers = allGroupItemsByGroup.map(
                            (x) => x[FieldNames.treatmentActivityGroupItems.sequenceNumber]
                        );
                        if (sequenceNumbers && sequenceNumbers.length > 0) {
                            nextSequenceNumber = Math.max(...sequenceNumbers) + 1;
                        }
                    }
                }

                const groupItem = {};
                groupItem[FieldNames.treatmentActivityGroupItems.treatmentActivityGroupItemsID] =
                    uuidv4();
                groupItem[FieldNames.treatmentActivityGroupItems.treatmentActivityGroupJoinID] =
                    group[FieldNames.treatmentActivityGroups.id];
                groupItem[FieldNames.treatmentActivityGroupItems.treatmentActivityGroupID] =
                    group[FieldNames.treatmentActivityGroups.globalID];
                groupItem[FieldNames.treatmentActivityGroupItems.joinID] = actualsIdVal;
                groupItem[FieldNames.treatmentActivityGroupItems.joinFeature] =
                    geometryType ?? FieldNames.joinFeatureValues.polygon;
                groupItem[FieldNames.treatmentActivityGroupItems.entityID] =
                    actualsFeatureResults[FieldNames.featureFields.objectID];
                groupItem[FieldNames.treatmentActivityGroupItems.entityType] = isActualTreatment
                    ? FieldNames.entityTypes.actualTreatment
                    : FieldNames.entityTypes.actualActivity;
                groupItem[FieldNames.featureFields.hasActual] = FieldNames.yesNo.yes;
                groupItem[FieldNames.treatmentActivityGroupItems.id] =
                    futureActualFeature[FieldNames.featureFields.id];
                groupItem[FieldNames.treatmentActivityGroupItems.sequenceNumber] =
                    nextSequenceNumber;
                groupItemsToSave.push(groupItem);
            });
            GroupingService.addItemsForGroup(groupItemsToSave)
                .then((resp) => {
                    setFeatureGroups(plannedGroupsAll);
                })
                .catch((error) => {
                    ErrorService.addErrorLogEntry(
                        error
                    );
                });
        }
    };

    const createFundingForActual = (geoID, geoType) => {
        let isPartialSuccess = false;
        let isFullSuccess = true;
        if (plannedFunding && plannedFunding.length > 0) {
            let fundingToSave = [];
            plannedFunding.forEach((funding) => {
                const fundingItem = _.cloneDeep(funding);
                fundingItem[FieldNames.fundingFields.joinID] = geoID;
                fundingItem[FieldNames.fundingFields.joinFeature] = geoType;
                fundingItem[FieldNames.fundingFields.modifiedBy] =
                    userInfo[FieldNames.userFields.nifcUsername];
                fundingItem[FieldNames.fundingFields.modifiedDate] = new Date().getTime();
                fundingItem[FieldNames.fundingFields.createdBy] =
                    userInfo[FieldNames.userFields.nifcUsername];
                fundingItem[FieldNames.fundingFields.createdDate] = new Date().getTime();
                fundingItem[FieldNames.fundingFields.fundingIsManual] = FieldNames.yesNo.no;
                fundingItem[FieldNames.fundingFields.isArchived] = FieldNames.yesNo.no;
                fundingToSave.push(fundingItem);
            });

            if (fundingToSave && fundingToSave.length) {
                FundingService.addFundingEntries(fundingToSave)
                    .then((actualFundingResp) => {
                        if (
                            actualFundingResp &&
                            actualFundingResp.addResults &&
                            actualFundingResp.addResults.length > 0
                        ) {
                            let i = 0;
                            actualFundingResp.addResults.forEach((fundResp) => {
                                if (fundResp && fundResp.success) {
                                    isPartialSuccess = true;
                                } else {
                                    isFullSuccess = false;
                                }
                                i++;
                            });
                        } else {
                            isFullSuccess = false;
                        }
                    })
                    .catch((error) => {
                        ErrorService.addErrorLogEntry(
                            error
                        );
                    });
            } else {
                isFullSuccess = false;
                isPartialSuccess = false;
            }
        }
        return { fullSuccess: isFullSuccess, partialSuccess: isPartialSuccess };
    };

    const createInteragencyFundingForActual = (geoID, geoType) => {
        let isPartialSuccess = false;
        let isFullSuccess = true;
        if (plannedInteragencySupport && plannedInteragencySupport.length > 0) {
            let interagencySupportToAdd = [];
            plannedInteragencySupport.forEach((interagencySupport) => {
                const interagencySupportItem = esriLang.clone(interagencySupport);
                interagencySupportItem[FieldNames.interagencyFields.joinID] = geoID;
                interagencySupportItem[FieldNames.interagencyFields.joinFeature] = geoType;
                interagencySupportItem[FieldNames.interagencyFields.modifiedBy] =
                    userInfo[FieldNames.userFields.nifcUsername];
                interagencySupportItem[FieldNames.interagencyFields.modifiedDate] =
                    new Date().getTime();
                interagencySupportItem[FieldNames.interagencyFields.createdBy] =
                    userInfo[FieldNames.userFields.nifcUsername];
                interagencySupportItem[FieldNames.interagencyFields.createdDate] =
                    new Date().getTime();
                interagencySupportItem[FieldNames.interagencyFields.interagencyName] =
                    interagencySupport[FieldNames.interagencyFields.interagencyName];
                interagencySupportToAdd.push(interagencySupportItem);
            });

            if (interagencySupportToAdd && interagencySupportToAdd.length) {
                InteragencyService.addMultipleInteragency(interagencySupportToAdd)
                    .then((actualInteragencyResp) => {
                        if (
                            actualInteragencyResp &&
                            actualInteragencyResp.addResults &&
                            actualInteragencyResp.addResults.length > 0
                        ) {
                            let i = 0;
                            actualInteragencyResp.addResults.forEach((interResp) => {
                                if (interResp && interResp.success) {
                                    isPartialSuccess = true;
                                } else {
                                    isFullSuccess = false;
                                }
                                i++;
                            });
                        } else {
                            isFullSuccess = false;
                        }
                    })
                    .catch((error) => {
                        ErrorService.addErrorLogEntry(
                            error
                        );
                    });
            } else {
                isFullSuccess = false;
                isPartialSuccess = false;
            }
        }
        return { fullSuccess: isFullSuccess, partialSuccess: isPartialSuccess };
    };

    const onSaveChanges = async () => {
        try {
            const isActualTreatment = isTreatment;
            dispatch(setShowGlobalLoader(true));
            setIsSaveDisabled(true);

            // Logic that applies for both edits and creation
            if (!editableFeature) {
                throw new Error('Unable to find Action. Please reload page and try again');
            }

            let perimeter = editableFeature?.geometry;
            if (!perimeter) {
                perimeter = editableFeature?.perimeter;
            }
            const geoType = geometryType ?? FieldNames.joinFeatureValues.polygon; // Failsafe

            if (!editableFeature.attributes[FieldNames.featureFields.initiationFiscalYear]) {
                editableFeature.attributes[FieldNames.featureFields.initiationFiscalYear] = null;
            }

            if (!editableFeature.attributes[FieldNames.featureFields.completionFiscalYear]) {
                editableFeature.attributes[FieldNames.featureFields.completionFiscalYear] = null;
            }

            if (!editableFeature.attributes[FieldNames.featureFields.cancelledDate]) {
                editableFeature.attributes[FieldNames.featureFields.cancelledDate] = null;
            }

            if (doClearCancelLogic) {
                const cancelFeature = await CancelService.getFeatureCancelReason(
                    editableFeature.attributes[FieldNames.cancelFields.joinFeature],
                    editableFeature.attributes[FieldNames.cancelFields.joinID]
                );

                if (cancelFeature) {
                    let cancelObject = {
                        OBJECTID: cancelFeature[FieldNames.featureFields.objectID],
                        ModifiedBy: userInfo[FieldNames.userFields.nifcUsername],
                        ModifiedDate: new Date().getTime(),
                        IsArchived: FieldNames.yesNo.yes,
                    };
                    const updateCancelResp =
                        await CancelService.updateCanceledFeatureEntry(cancelObject);

                    setDoClearCancelLogic(false);
                }
            }

            if (!editableFeature.attributes[FieldNames.featureFields.durability]) {
                editableFeature.attributes[FieldNames.featureFields.durability] = null;
            }

            if (!editableFeature.attributes[FieldNames.featureFields.class]) {
                editableFeature.attributes[FieldNames.featureFields.class] = isActualTreatment
                    ? FieldNames.classTypes.actualTreatment
                    : FieldNames.classTypes.actualActivity;
            }

            editableFeature.attributes[FieldNames.featureFields.lastModifiedDate] =
                new Date().getTime();
            editableFeature.attributes[FieldNames.featureFields.modifiedBy] = userInfo
                ? userInfo[FieldNames.userFields.nifcUsername]
                : '';

            const tempEditableFeature = buildCostCode(editableFeature, FieldNames.featureFields);

            // checking approved status here is a hack b/c initial status setting does not stick bugs it is poorly handled above
            if (
                plannedFeature.attributes[FieldNames.featureFields.isProgressiveReporting] !==
                    FieldNames.yesNo.yes ||
                plannedFeature.attributes[FieldNames.featureFields.status]?.includes('Approved')
            ) {
                dispatch(updatePlannedFeatureAttributes({ [FieldNames.featureFields.status]: tempEditableFeature?.attributes?.[FieldNames.featureFields.status]}))
            }

            if (tempEditableFeature?.attributes && isNewFeature) // We are creating a new actual
            {
                onSaveNewChanges(
                    perimeter,
                    geoType,
                    tempEditableFeature,
                    isActualTreatment,
                    plannedFeature
                );
            } else if (tempEditableFeature?.attributes) // we are editing an actual
            {
                onSaveUpdateChanges(perimeter, geoType, tempEditableFeature, plannedFeature);
            }
        } catch (error) {
            setIsSaveDisabled(false);
            dispatch(setShowGlobalLoader(false));
            ErrorService.addErrorLogEntry(error);
            setSnackbarOpen(true);
            setSnackbarSeverity('error');
            if (isTreatment) {
                setSnackbarMessage(
                    "The Actual Treatment wasn't added/updated. Please close and try again."
                );
            } else {
                setSnackbarMessage(
                    "The Actual Activity wasn't added/updated. Please close and try again."
                );
            }
        }
    };

    const onSaveNewChanges = async (
        perimeter,
        geoType,
        tempEditableFeature,
        isActualTreatment,
        plannedFeature
    ) => {
        try {
            if (tempEditableFeature?.attributes && isNewFeature) // We are creating a new actual
            {
                let futureActualFeature = tempEditableFeature?.attributes;
                futureActualFeature[FieldNames.featureFields.createdOnDate] = new Date().getTime();
                futureActualFeature[FieldNames.featureFields.createdBy] = userInfo
                    ? userInfo[FieldNames.userFields.nifcUsername]
                    : '';
                futureActualFeature[FieldNames.featureFields.hasGroup] =
                    plannedGroups?.Groups?.length > 0 ? FieldNames.yesNo.yes : FieldNames.yesNo.no;
                futureActualFeature[FieldNames.featureFields.groupCount] =
                    plannedGroups?.Groups?.length > 0 ? plannedGroups.Groups.length : 0;
                const geoAddResponse = await GeometryService.addGeometry(
                    perimeter,
                    geoType,
                    userInfo[FieldNames.userFields.nifcUsername]
                );
                if (geoAddResponse?.addResults?.length > 0) {
                    const geoObjectID = geoAddResponse?.addResults[0]?.objectId;
                    const geoID = await IDService.getGeometryID(geoObjectID, geoType); //TODO: Add to state variable so we don't need to call geometry multiple times

                    futureActualFeature[FieldNames.featureFields.joinID] = geoID;
                    futureActualFeature[FieldNames.featureFields.joinFeature] = geoType;
                    futureActualFeature[FieldNames.featureFields.joinObjectID] = geoObjectID;
                    const isPostFire = isFeaturePostFire(futureActualFeature);
                    const response = await FeatureService.addFeature(futureActualFeature);
                    setSnackbarOpen(true);
                    emitCustomEvent(Events.DisableDrawMode);
                    if (
                        isPostFire &&
                        (futureActualFeature[FieldNames.featureFields.unitOfMeasure] ||
                            futureActualFeature[FieldNames.featureFields.measureAmount])
                    ) {
                        const measurementResponse = await addMeasurementEntry(
                            futureActualFeature,
                            geoType,
                            geoID,
                            true,
                        );
                        if (measurementResponse?.addResults[0].objectId) {
                            futureActualFeature[FieldNames.featureFields.measurementOBJECTID] =
                                measurementResponse?.addResults[0].objectId;
                        }
                    }

                    const wasSuccess = createFundingForActual(geoID, geoType);
                    createGroupItemsForActual(
                        true,
                        response.addResults[0],
                        futureActualFeature,
                        geoID
                    );

                    const wasInteragencySuccess = createInteragencyFundingForActual(geoID, geoType);

                    const updatedPlannedFeatureAttributes = {
                        ...plannedFeature.attributes,
                        [FieldNames.featureFields.hasActual]: FieldNames.yesNo.yes
                    }
                    const updatedPlannedFeature = {
                        ...plannedFeature,
                        attributes: updatedPlannedFeatureAttributes
                    }

                    dispatch(updatePlannedFeatureAttributes({ [FieldNames.featureFields.hasActual]: FieldNames.yesNo.yes}))

                    const estimateResponse = await FeatureService.updateFeature(updatedPlannedFeature);

                    const newPlannedActualsItem = {};
                    newPlannedActualsItem[FieldNames.estimatedActuals.joinIDE] =
                        plannedFeature.attributes[FieldNames.featureFields.joinID];
                    newPlannedActualsItem[FieldNames.estimatedActuals.joinIDA] = geoID;
                    newPlannedActualsItem[FieldNames.estimatedActuals.joinFeatureE] =
                        plannedFeature.attributes[FieldNames.featureFields.joinFeature];
                    newPlannedActualsItem[FieldNames.estimatedActuals.joinFeatureA] = geometryType;
                    newPlannedActualsItem[FieldNames.estimatedActuals.createdBy] =
                        userInfo[FieldNames.userFields.nifcUsername];
                    newPlannedActualsItem[FieldNames.estimatedActuals.createdDate] =
                        new Date().getTime();
                    const plannedActualResp =
                        await PlannedActualsService.createPlannedActualFeature(
                            newPlannedActualsItem
                        );

                    // If this is a postfire treatment, copy stored postfire values to the new actual
                    if (isFeaturePostFire(futureActualFeature)) {
                        emitCustomEvent(Events.CopyStoredPostFireValuesToActual, {
                            newJoinId: geoID,
                            newJoinFeature: geoType
                        });
                    }

                    const auditResponse = await addFeatureAuditLogEntry(
                        tempEditableFeature,
                        geoID,
                        geoType,
                        true,
                        true
                    ).catch((error) => {
                        ErrorService.addErrorLogEntry(
                            error
                        );
                    });

                    setSnackbarSeverity('success');
                    dispatch(setListCheckedFeatures([]));
                    setIsSaveDisabled(false);
                    dispatch(setShowGlobalLoader(false));
                    dispatch(setIsNewFeature(false));
                    emitCustomEvent(Events.ClearModifiedLayer);
                    setSnackbarSeverity('success');
                    setSnackbarMessage(
                        isTreatment ? 'Added new Actual Treatment.' : 'Added new Actual Activity.'
                    );
                    setSnackbarOpen(true);
                    let entityStringValue = isPostFire ? 'pf' : 'f';
                    if (isTreatment) {
                        navigate(
                            `/${entityStringValue}/treatments/${response?.addResults[0]?.objectId}`,
                        );
                    } else {
                        navigate(
                            `/${entityStringValue}/activities/${response.addResults[0].objectId}`,
                        );
                    }
                    emitCustomEvent(Events.RefreshLayer, {
                        geo: perimeter,
                        geometryType: geometryType,
                        isPostFire: isPostFire,
                        featureClass: isTreatment
                            ? FieldNames.classTypes.actualTreatment
                            : FieldNames.classTypes.actualActivity,
                        attributes: futureActualFeature.attributes,
                        isProject,
                    });
                }
            }
        } catch (error) {
            setIsSaveDisabled(false);
            dispatch(setShowGlobalLoader(false));
            ErrorService.addErrorLogEntry(error);
            setSnackbarOpen(true);
            setSnackbarSeverity('error');
            if (isTreatment) {
                setSnackbarMessage(
                    "The Actual Treatment wasn't added/updated. Please close and try again."
                );
            } else {
                setSnackbarMessage(
                    "The Actual Activity wasn't added/updated. Please close and try again."
                );
            }
        }
    };

    const onSaveUpdateChanges = async (perimeter, geoType, tempEditableFeature, plannedFeature) => {
        try {
            if (tempEditableFeature?.attributes) // we are editing an actual
            {
                const geoObjectID = tempEditableFeature?.attributes
                    ? tempEditableFeature?.attributes[FieldNames.featureFields.joinObjectID]
                    : null;
                const geoUpdateResponse = await GeometryService.updateGeometry(
                    perimeter,
                    geoType,
                    userInfo[FieldNames.userFields.nifcUsername],
                    geoObjectID
                );
                const geoID = tempEditableFeature?.attributes
                    ? tempEditableFeature?.attributes[FieldNames.featureFields.joinID]
                    : null;
                const response = await FeatureService.updateFeature(tempEditableFeature);
                let measurementResponse = null;
                const isPostFire = isFeaturePostFire(tempEditableFeature.attributes);
                if (tempEditableFeature.attributes[FieldNames.featureFields.measurementOBJECTID]) {
                    if (
                        isPostFire &&
                        (tempEditableFeature?.attributes[FieldNames.featureFields.unitOfMeasure] ||
                            tempEditableFeature?.attributes[FieldNames.featureFields.measureAmount])
                    ) {
                        measurementResponse = await addMeasurementEntry(
                            tempEditableFeature,
                            geoType,
                            geoID,
                            false,
                        );
                    }
                } else {
                    if (
                        isPostFire &&
                        (tempEditableFeature?.attributes[FieldNames.featureFields.unitOfMeasure] ||
                            tempEditableFeature?.attributes[FieldNames.featureFields.measureAmount])
                    ) {
                        measurementResponse = await addMeasurementEntry(
                            tempEditableFeature,
                            geoType,
                            geoID,
                            true,
                        );
                        if (measurementResponse?.addResults[0].objectId) {
                            tempEditableFeature.attributes[
                                FieldNames.featureFields.measurementOBJECTID
                            ] = measurementResponse?.addResults[0].objectId;
                        }
                    }
                }
                const auditResp = await addFeatureAuditLogEntry(
                    tempEditableFeature,
                    tempEditableFeature.attributes[FieldNames.featureFields.joinID],
                    tempEditableFeature.attributes[FieldNames.featureFields.joinFeature],
                    false,
                    true
                ).catch((error) => {
                    ErrorService.addErrorLogEntry(
                        error
                    );
                });

                const plannedResponse = await FeatureService.updateFeature(
                    plannedFeature?.attributes
                );
                const plannedAuditResp = await addFeatureAuditLogEntry(
                    plannedFeature,
                    plannedFeature.attributes[FieldNames.featureFields.joinID],
                    plannedFeature.attributes[FieldNames.featureFields.joinFeature],
                    false,
                    false
                ).catch((error) => {
                    ErrorService.addErrorLogEntry(
                        error
                    );
                });

                setIsSaveDisabled(false);
                dispatch(setShowGlobalLoader(false));
                setEditableFeature(tempEditableFeature);
                dispatch(setFeature(tempEditableFeature));
                setBaseFeatureAttributes(_.cloneDeep(tempEditableFeature.attributes));
                emitCustomEvent(Events.ClearModifiedLayer);
                setSnackbarSeverity('success');
                setSnackbarMessage(
                    isTreatment
                        ? 'Treatment changes saved successfully.'
                        : 'Activity changes saved successfully.'
                );

                if (isTreatment) {
                    dispatch(resetActualsTreatmentValidationState());
                } else {
                    dispatch(resetActualsActivityValidationState());
                }
                setSnackbarOpen(true);
                emitCustomEvent(Events.RefreshLayer, {
                    geo: perimeter,
                    geometryType: geometryType,
                    isPostFire: isPostFire,
                    featureClass: isTreatment
                        ? FieldNames.classTypes.actualTreatment
                        : FieldNames.classTypes.actualActivity,
                    attributes: tempEditableFeature.attributes,
                    isProject,
                });
                emitCustomEvent(Events.ClearModifiedLayer);
            }
        } catch (error) {
            setIsSaveDisabled(false);
            dispatch(setShowGlobalLoader(false));
            ErrorService.addErrorLogEntry(error);
            setSnackbarOpen(true);
            setSnackbarSeverity('error');
            if (isTreatment) {
                setSnackbarMessage(
                    "The Actual Treatment wasn't added/updated. Please close and try again."
                );
            } else {
                setSnackbarMessage(
                    "The Actual Activity wasn't added/updated. Please close and try again."
                );
            }
        }
    };

    const buildCostCode = (editableFeature, fields) => {
        let wbsCode = editableFeature.attributes[FieldNames.featureFields.wbsID];
        if (!wbsCode) {
            wbsCode = 'XXXXXXXXXXXX';
            editableFeature.attributes[FieldNames.featureFields.wbsID] = wbsCode;
        }

        let functionalArea = editableFeature.attributes[FieldNames.featureFields.functionalArea];
        if (!functionalArea) {
            functionalArea = 'XXXXXXXXX.XXXXXX';
            editableFeature.attributes[FieldNames.featureFields.functionalArea] = functionalArea;
        }

        let costCenter = editableFeature.attributes[FieldNames.featureFields.costCenter];
        if (!costCenter) {
            costCenter = 'XXXXXXXXXX';
            editableFeature.attributes[FieldNames.featureFields.costCenter] = costCenter;
        }

        editableFeature.attributes[FieldNames.featureFields.costCode] =
            `${costCenter}/${functionalArea}/${wbsCode}`;
        return editableFeature;
    };

    const onClose = () => {
        dispatch(setMode(FieldNames.modes.read));
        emitCustomEvent(Events.DisableExistingIcons, true);
        emitCustomEvent(Events.DisableNewActualIcons, true);
        emitCustomEvent(Events.DisableNewEstimatedIcons, false);
        dispatch(setFeature(null));
        if (!isTreatment) {
            navigate('/home');
        } else {
            navigate('/home');
        }
    };

    const getAllUnsavedFieldsCount = (feature) => {
        let count = 0;
        if (feature) {
            groupDetails?.map((detail) => (count += getUnsavedFieldsCountByGroup(feature, detail)));
        }

        return count <= 0 ? 0 : count;
    };

    const getAllApprovalFieldsCount = (feature, isRequired) => {
        let approvals = false;
        if (feature?.attributes) {
            if (
                isRequired &&
                feature.attributes[FieldNames.featureFields.class] !==
                    FieldNames.classTypes.actualActivity &&
                feature.attributes[FieldNames.featureFields.class] !==
                    FieldNames.classTypes.actualTreatment
            ) {
                approvals = true;
            }
        }
        return approvals;
    };

    const getMissingFieldsCountByGroup = (feature, detail) => {
        let count = 0;
        let remainingReqFields = [];
        if (feature?.attributes && detail) {
            const missingFields = getAllMissingFields(feature, isTreatment, true, null);
            if (
                detail.fields &&
                missingFields &&
                detail.fields.length > 0 &&
                missingFields.length > 0
            ) {
                detail.fields.forEach((element) => {
                    if (missingFields.includes(element)) {
                        remainingReqFields.push(element);
                    }
                });
            } else {
                remainingReqFields = [];
            }
            count = remainingReqFields.length;
        }
        return count <= 0 ? 0 : count;
    };

    const getUnsavedFieldsCountByGroup = (feature, detail) => {
        let count = 0;
        let fieldsUnsavedInGroup = [];
        let unsavedFields = {};
        if (feature && detail) {
            if (isTreatment) {
                // Estimated Treatment
                unsavedFields = treatmentValidationUnsavedValues;
            } else {
                // Estimated Activity
                unsavedFields = activityValidationUnsavedValues;
            }

            for (let prop in unsavedFields) {
                if (unsavedFields[prop] === false) {
                    continue;
                } else if (detail?.fields?.length > 0 && detail?.fields.includes(prop)) {
                    fieldsUnsavedInGroup.push(prop);
                }
            }
            count = fieldsUnsavedInGroup.length;
        }
        return count <= 0 ? 0 : count;
    };

    const isInGroup = (detail, item) => {
        let isInCurrentGroup = false;
        if (item && detail) {
            const requiredFields = getRequiredFields(
                item,
                isTreatment,
                detail.fields[FieldNames.featureFields.fundingAgency],
                true
            );
            if (
                requiredFields &&
                detail.fields &&
                detail.fields.length > 0 &&
                requiredFields.length > 0
            ) {
                detail.fields.forEach((element) => {
                    if (requiredFields.includes(element)) {
                        isInCurrentGroup = true;
                    }
                });
            }
        }
        return isInCurrentGroup;
    };

    const chipClicked = () => {
        let groupings = FormGroupings.treatmentDetails;
        if (!isTreatment) {
            groupings = FormGroupings.activityDetails;
        }
        let itemsToOpen = [];
        let updatedValue = {};
        groupings.forEach((element) => {
            const count = getMissingFieldsCountByGroup(editableFeature, element);
            if (count > 0) {
                updatedValue[element.identifier] = 'true';
                itemsToOpen.push(element.identifier);
            } else {
                updatedValue[element.identifier] = 'false';
            }
        });

        setExpandedItems((expandedItems) => ({
            ...expandedItems,
            ...updatedValue,
        }));

        if (itemsToOpen.length > 0) {
            setTimeout(() => {
                const el = document.getElementById(itemsToOpen[0]);
                if (el) {
                    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
            }, 300);
        }
    };

    const chipClickedUnsaved = () => {
        let groupings = FormGroupings.treatmentDetails;
        if (!isTreatment) {
            groupings = FormGroupings.activityDetails;
        }
        let itemsToOpen = [];
        let updatedValue = {};
        groupings.forEach((element) => {
            const count = getUnsavedFieldsCountByGroup(editableFeature, element);
            if (count > 0) {
                updatedValue[element.identifier] = 'true';
                itemsToOpen.push(element.identifier);
            } else {
                updatedValue[element.identifier] = 'false';
            }
        });

        setExpandedItems((expandedItems) => ({
            ...expandedItems,
            ...updatedValue,
        }));
    };

    const navigateToEstimated = (estimatedObjectID, isFeaturePostFire) => {
        if (estimatedObjectID !== null) {
            const entityStringValue = isFeaturePostFire ? 'pf' : 'f';
            if (isTreatment) {
                navigate(`/${entityStringValue}/treatments/${estimatedObjectID}`, {
                });
            } else {
                navigate(`/${entityStringValue}/activities/${estimatedObjectID}`, {
                });
            }
            dispatch(setIsActual(false));
        }
    };

    const getDetailFields = (detail, feature) => {
        let detailFields = [];
        if (detail && groupDetails) {
            const isPostFire = isFeaturePostFire(feature?.attributes);
            let currentDetail = groupDetails?.filter((x) => x.identifier === detail.identifier);

            if (currentDetail.length > 0) {
                detailFields = currentDetail[0].fields;
            }

            if (detail.identifier === 'proposalPanel' && isPostFire) {
                detailFields = detailFields.filter(
                    (x) =>
                        x !== FieldNames.featureFields.isInteragency &&
                        x !== FieldNames.featureFields.interagencySupport
                );
            }

            if (
                (detail.title === 'Context' || detail.title === 'Costs') &&
                feature &&
                feature.attributes &&
                feature.attributes[FieldNames.featureFields.fundingAgency] &&
                !feature.attributes[FieldNames.featureFields.fundingAgency].includes(
                    FieldNames.agencies.blm
                )
            ) {
                detailFields = detailFields.filter(
                    (x) =>
                        x !== FieldNames.featureFields.fundingSubUnit &&
                        x !== FieldNames.featureFields.treatmentDriver
                );
            }

            if (
                detail.title === 'Costs' &&
                feature &&
                feature.attributes &&
                feature.attributes[FieldNames.featureFields.fundingAgency] &&
                !feature.attributes[FieldNames.featureFields.fundingAgency].includes(
                    FieldNames.agencies.bia
                )
            ) {
                detailFields = detailFields.filter(
                    (x) =>
                        x !== FieldNames.featureFields.fundingTribe &&
                        x !== FieldNames.featureFields.isRTRL
                );
            }

            if (
                detail.title === 'Costs' &&
                feature &&
                feature.attributes &&
                !feature.attributes[FieldNames.featureFields.fundingAgency]
            ) {
                detailFields = detailFields.filter(
                    (x) =>
                        x !== FieldNames.featureFields.fundingTribe &&
                        x !== FieldNames.featureFields.isRTRL &&
                        x !== FieldNames.featureFields.fundingSubUnit &&
                        x !== FieldNames.featureFields.treatmentDriver
                );
            }

            if (isFeaturePostFire(feature?.attributes)) {
                detailFields = detailFields.filter(
                    (x) =>
                        x !== FieldNames.featureFields.isWUI &&
                        x !== FieldNames.featureFields.isVegetationManual &&
                        x !== FieldNames.featureFields.vegDeparturePercentageDerived &&
                        x !== FieldNames.featureFields.vegDeparturePercentageManual &&
                        x !== FieldNames.featureFields.durability &&
                        x !== FieldNames.featureFields.isRTRL
                );
            } else {
                detailFields = detailFields.filter(
                    (x) =>
                        x !== FieldNames.featureFields.estimatedEquipmentCost &&
                        x !== FieldNames.featureFields.estimatedMaterialAndSupplyCost &&
                        x !== FieldNames.featureFields.estimatedProcurementCost &&
                        x !== FieldNames.featureFields.estimatedTravelCost
                );
            }

            //We need to check the planned too - If the actual doesn't have subTypes, but the planned does still show subtype
            const subTypePossibleValues = getByLevel(
                feature?.attributes,
                FieldNames.categoryAssociationFields.subTypes
            );
            const subTypePossibleValuesByPlanned = getByLevel(
                plannedFeature?.attributes,
                FieldNames.categoryAssociationFields.subTypes
            );
            if (
                (!subTypePossibleValues || subTypePossibleValues?.length === 0) &&
                (!subTypePossibleValuesByPlanned || subTypePossibleValuesByPlanned?.length === 0)
            ) {
                detailFields = detailFields.filter((x) => x !== FieldNames.featureFields.subType);
            }
        }
        return detailFields;
    };

    const formLabel = (editableFeature, field) => {
        requiredFields = getRequiredFields(
            editableFeature,
            isTreatment,
            editableFeature?.attributes[FieldNames.featureFields.fundingAgency],
            true
        );
        if (!requiredFields?.includes(FieldNames.featureFields.wbsID)) {
            requiredFields.push(FieldNames.featureFields.wbsID);
        }

        if (
            editableFeature &&
            field === FieldNames.featureFields.statusReason &&
            editableFeature.attributes[FieldNames.featureFields.status] !==
                FieldNames.statuses.cancelled
        ) {
            return <span></span>;
        } else {
            if (field === FieldNames.featureFields.id) {
                return <span className='required'>*</span>;
            } else if (requiredFields && requiredFields.includes(field)) {
                return <span className='required'>*</span>;
            } else {
                return <span></span>;
            }
        }
    };

    const labelsForBoth = (editableFeature, detail, field) => {
        const currentfieldInfo = fieldsDefinition[field];
        let staticFieldName = currentfieldInfo ? currentfieldInfo.alias : '**' + field;
        const isPostFire = isFeaturePostFire(editableFeature?.attributes);
        if (field === FieldNames.featureFields.id) {
            staticFieldName = currentfieldInfo ? 'ID' : '**ID';
        } else if (field === FieldNames.featureFields.name) {
            staticFieldName = currentfieldInfo ? 'Name' : '**Name';
        } else if (field === FieldNames.featureFields.priority) {
            staticFieldName = currentfieldInfo ? 'Priority' : '**Priority';
        } else if (field === FieldNames.featureFields.wbsID) {
            staticFieldName = currentfieldInfo ? 'WBS' : '**WBS';
        } else if (field === FieldNames.featureFields.vegDeparturePercentageManual) {
            staticFieldName = 'Vegetation Departure Index';
        } else if (field === 'ManageCosts') {
            staticFieldName = 'Manage Costs';
        }
        if (
            !isPostFire &&
            (field === FieldNames.featureFields.measureAmount ||
                field === FieldNames.featureFields.unitOfMeasure ||
                (field === FieldNames.featureFields.interagencySupport &&
                    editableFeature?.attributes?.[FieldNames.featureFields.isInteragency] !==
                        FieldNames.yesNo.yes))
        ) {
            return '';
        } else {
            return (
                <Grid
                    item
                    size={12}
                    container
                    direction='column'
                    className='grid-item'
                    style={{ alignItems: 'left', paddingRight: '5px' }}
                >
                    <Grid
                        item
                        size={6}
                        style={{ padding: '5px 2px 5px 2px', whiteSpace: 'pre-wrap' }}
                    >
                        <label
                            style={{
                                color: `${ColorConstants.Default}`,
                                textTransform: 'uppercase',
                                fontSize: '0.95rem',
                            }}
                        >
                            {staticFieldName}
                            {formLabel(editableFeature, field)}
                        </label>
                        <DetailsPopover
                            field={field}
                            isBulkApproval={false}
                        />
                    </Grid>
                </Grid>
            );
        }
    };

    const handleError = (resp) => {
        if (isTreatment) {
            setSnackbarMessage('Error deleting Actual Treatment!');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        } else {
            setSnackbarMessage('Error deleting Actual Activity!');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        }
    };

    const onCloseManageCosts = () => {
        setCostFundingPopoverOpen(false);
        if (
            editableFeature?.attributes?.[FieldNames.featureFields.joinID] &&
            editableFeature?.attributes?.[FieldNames.featureFields.joinFeature]
        ) {
            getFeatureAuditLog(
                editableFeature?.attributes?.[FieldNames.featureFields.joinID],
                editableFeature?.attributes?.[FieldNames.featureFields.joinFeature]
            );
        }
    };

    const archiveGroups = () => {
        const groupIds = featureGroups.map(
            (x) => x[FieldNames.treatmentActivityGroupItems.treatmentActivityGroupJoinID]
        );
        if (groupIds && groupIds.length > 0) {
            GroupingService.getAllGroupItemsByGroupIds(groupIds)
                .then((items) => {
                    const groupOccurrences = findOcc(
                        items,
                        FieldNames.treatmentActivityGroupItems.treatmentActivityGroupJoinID
                    );
                    const deleteGroupIds = [];
                    const deleteGroups = [];
                    groupOccurrences.forEach((occ) => {
                        if (occ.occurrence < 3) {
                            deleteGroupIds.push(
                                occ[
                                    FieldNames.treatmentActivityGroupItems
                                        .treatmentActivityGroupJoinID
                                ]
                            );
                        }
                    });
                    GroupingService.getGroupsByIDs(deleteGroupIds).then((groups) => {
                        groups.forEach((group) => {
                            group[FieldNames.treatmentActivityGroups.isArchived] = 'Yes';
                            const obj = {
                                attributes: group,
                            };
                            deleteGroups.push(obj);
                        });
                        GroupingService.archiveGroups(deleteGroups).catch((error) => {
                            ErrorService.addErrorLogEntry(
                                error
                            );
                        });
                    });
                })
                .catch((error) => {
                    ErrorService.addErrorLogEntry(
                        error
                    );
                });
        }
    };

    const archiveGroupItems = () => {
        if (associatedCount > 0) {
            let deleteGroupItem = [];
            if (
                editableFeature?.attributes &&
                editableFeature?.attributes[FieldNames.featureFields.joinID]
            ) {
                let geoType =
                    editableFeature?.attributes?.[FieldNames.featureFields.joinFeature] ??
                    FieldNames.joinFeatureValues.polygon;
                GroupingService.getAllGroupItemsForFeatures(
                    [editableFeature.attributes[FieldNames.featureFields.joinID]],
                    geoType
                ).then((result) => {
                    if (result && result.GroupItems && result.GroupItems.length > 0) {
                        result.GroupItems.forEach((groupItem) => {
                            groupItem[FieldNames.treatmentActivityGroupItems.isArchived] = 'Yes';
                            const obj = {
                                attributes: groupItem,
                            };
                            deleteGroupItem.push(obj);
                        });
                        GroupingService.archiveGroupItems(deleteGroupItem).catch((error) => {
                            ErrorService.addErrorLogEntry(error);
                        });
                    }
                });
            }
        }
    };

    const onHandleActualDeleteClicked = () => {
        dispatch(setShowGlobalLoader(true));
        if (
            plannedActualFeature?.attributes &&
            editableFeature?.attributes &&
            plannedFeature?.attributes
        ) {
            plannedActualFeature.attributes[FieldNames.featureFields.isArchived] =
                FieldNames.yesNo.yes;
            PlannedActualsService.updatePlannedActualFeature(plannedActualFeature)
                .then(async (response) => {
                    if (response.updateResults[0].success) {
                        const records = await getActualRecords(
                            plannedFeature.attributes[FieldNames.featureFields.joinID],
                            plannedFeature.attributes[FieldNames.featureFields.joinFeature]
                        );
                        dispatch(updatePlannedFeatureAttributes({ [FieldNames.featureFields.hasActual]: FieldNames.yesNo.no}))

                        const plannedFeatureMissingFieldsCount = getAllMissingFieldsCount(
                            plannedFeature,
                            isTreatment,
                            false,
                        );
                        const plannedFeatureStatus = getStatus(
                            plannedFeature,
                            records.length > 0,
                            false,
                            plannedFeatureMissingFieldsCount
                        );
                        dispatch(updatePlannedFeatureAttributes({ [FieldNames.featureFields.status]: plannedFeatureStatus}))
                        const tempPlannedFeature = _.cloneDeep(plannedFeature);
                        tempPlannedFeature.attributes[FieldNames.featureFields.status] = plannedFeatureStatus;
                        tempPlannedFeature.attributes[FieldNames.featureFields.hasActual] = FieldNames.yesNo.no;
                        FeatureService.updateFeature(tempPlannedFeature.attributes).then(
                            (plannedFeatureUpdate) => {
                                if (plannedFeatureUpdate.updateResults[0].success) {
                                    editableFeature.attributes[
                                        FieldNames.featureFields.isArchived
                                    ] = FieldNames.yesNo.yes;
                                    if (
                                        editableFeature.attributes[
                                            FieldNames.featureFields.completionFiscalYear
                                        ] === ''
                                    ) {
                                        editableFeature.attributes[
                                            FieldNames.featureFields.completionFiscalYear
                                        ] = null;
                                    }
                                    if (
                                        editableFeature.attributes[
                                            FieldNames.featureFields.initiationFiscalYear
                                        ] === ''
                                    ) {
                                        editableFeature.attributes[
                                            FieldNames.featureFields.initiationFiscalYear
                                        ] = null;
                                    }
                                    // Updating the actual
                                    const isPostFire = isFeaturePostFire(
                                        editableFeature?.attributes
                                    );
                                    FeatureService.updateFeature(editableFeature.attributes)
                                        .then((finalResponse) => {
                                            if (finalResponse.updateResults[0].success) {
                                                AuditLogService.addAuditLogEntry(
                                                    editableFeature.attributes,
                                                    editableFeature.attributes[
                                                        FieldNames.featureFields.joinID
                                                    ],
                                                    editableFeature.attributes[
                                                        FieldNames.featureFields.joinFeature
                                                    ],
                                                    userInfo[FieldNames.userFields.nifcUsername],
                                                    [`Feature was manually archived`]
                                                ).then((auditResp) => {
                                                    setSnackbarSeverity('success');
                                                    dispatch(setMode(FieldNames.modes.read));

                                                    setSnackbarMessage(
                                                        isTreatment
                                                            ? 'Actual Treatment Deleted.'
                                                            : 'Actual Activity Deleted.'
                                                    );
                                                    setSnackbarOpen(true);
                                                    navigate('/home');

                                                    let isError = false;
                                                    if (associatedCount > 0) {
                                                        try {
                                                            archiveGroupItems();
                                                            archiveGroups();
                                                        } catch (err) {
                                                            if (err) {
                                                                isError = true;
                                                            }
                                                        }
                                                    }
                                                    emitCustomEvent(Events.RefreshLayer, {
                                                        geo: editableFeature.geometry,
                                                        geometryType: geometryType,
                                                        isPostFire: isPostFire,
                                                        featureClass: isTreatment
                                                            ? FieldNames.classTypes.actualTreatment
                                                            : FieldNames.classTypes.actualActivity,
                                                        attributes: editableFeature.attributes,
                                                        isProject,
                                                    });
                                                    emitCustomEvent(Events.ClearModifiedLayer);
                                                });
                                            } else {
                                                handleError(finalResponse);
                                            }
                                            dispatch(setShowGlobalLoader(false));
                                        })
                                        .catch((error) => {
                                            ErrorService.addErrorLogEntry(
                                                error
                                            );
                                            dispatch(setShowGlobalLoader(false));
                                        });
                                } else {
                                    handleError(plannedFeatureUpdate);
                                    dispatch(setShowGlobalLoader(false));
                                }
                            }
                        );
                    } else {
                        handleError(response);
                        dispatch(setShowGlobalLoader(false));
                    }
                })
                .catch((error) => {
                    ErrorService.addErrorLogEntry(
                        error
                    );
                    dispatch(setShowGlobalLoader(false));
                });
        }
    };

    const isFieldHighlighted = (field) => {
        let isHighlighted = false;
        if (field === FieldNames.featureFields.id) {
            isHighlighted = true;
        } else {
            if (
                plannedEditableFeature &&
                plannedEditableFeature.attributes &&
                plannedEditableFeature.attributes[field] &&
                editableFeature?.attributes &&
                editableFeature.attributes[field]
            ) {
                if (
                    plannedEditableFeature.attributes[field] === editableFeature.attributes[field]
                ) {
                    isHighlighted = false;
                } else {
                    isHighlighted = true;
                }
            }
        }
        return isHighlighted;
    };

    const tableRowUI = (detail, feature) => {
        //Estimated detail fields
        const detailFields = getDetailFields(detail, feature);
        const isPostFire = isFeaturePostFire(feature?.attributes);
        return (
            <Fragment>
                {detailFields.map((field, i) => (
                    <Fragment key={i}>
                        {(isPostFire ||
                            (!isPostFire &&
                                field !== FieldNames.featureFields.measureAmount &&
                                field !== FieldNames.featureFields.unitOfMeasure)) && (
                            <TableRow key={`${field}Row`}>
                                {field && field.substring(0, 3) === '*H*' ? (
                                    <>
                                        <Typography
                                            key={i}
                                            variant='h5'
                                            sx={{ textDecoration: 'underline' }}
                                        >
                                            {field.substring(3)}
                                        </Typography>
                                    </>
                                ) : (
                                    <>
                                        <TableCell
                                            component='th'
                                            scope='row'
                                            padding={'none'}
                                            width={'33%'}
                                            style={{ fontSize: '1rem' }}
                                        >
                                            {labelsForBoth(
                                                feature,
                                                detail,
                                                field
                                            )}
                                        </TableCell>
                                        <TableCell
                                            component='th'
                                            scope='row'
                                            padding={'none'}
                                            width={'33%'}
                                            style={{ maxWidth: 10, fontSize: '1rem' }}
                                        >
                                            <DetailsPlannedGridItem
                                                key={i}
                                                field={field}
                                                plannedFeature={isNewFeature ? plannedEditableFeature : plannedFeature } // TODO - Overloads!
                                                isFieldHighlighted={isFieldHighlighted(field)}
                                                setCostFundingPopoverOpen={
                                                    setCostFundingPopoverOpen
                                                }
                                                setCostFundingPopoverUsePlannedActual={
                                                    setCostFundingPopoverUsePlannedActual
                                                }
                                                interagencySelected={interagencySelected}
                                                setInteragencySelected={setInteragencySelected}
                                                otherInteragencySelected={otherInteragencySelected}
                                                setOtherInteragencySelected={
                                                    setOtherInteragencySelected
                                                }
                                            />
                                        </TableCell>
                                        <TableCell
                                            component='th'
                                            scope='row'
                                            padding={'none'}
                                            width={'33%'}
                                            style={{ maxWidth: 10, fontSize: '1rem' }}
                                        >
                                            <DetailsActualsGridItem
                                                key={i}
                                                field={field}
                                                actualsDetail={editableFeature}
                                                onFieldChange={onHandleAttributeChanged}
                                                getAllMissingFieldsCount={getAllMissingFieldsCount}
                                                getAllUnsavedFieldsCount={getAllUnsavedFieldsCount}
                                                getAllApprovalFieldsCount={
                                                    getAllApprovalFieldsCount
                                                }
                                                locationAgencyValue={locationAgencyValue}
                                                setLocationAgencyValue={setLocationAgencyValue}
                                                locationRegionValue={locationRegionValue}
                                                setLocationRegionValue={setLocationRegionValue}
                                                locationUnitValue={locationUnitValue}
                                                setLocationUnitValue={setLocationUnitValue}
                                                tribeNameValue={tribeNameValue}
                                                setTribeNameValue={setTribeNameValue}
                                                classValue={classValue}
                                                setClassValue={setClassValue}
                                                categoryValue={categoryValue}
                                                setCategoryValue={setCategoryValue}
                                                isCancelled={isCancelled}
                                                onFiscalFieldChange={onHandleDateWithFYFQChanged}
                                                departureIndexOptions={departureIndexOptions}
                                                statusOptions={statusOptions}
                                                requiredFields={
                                                    editableFeature?.attributes
                                                        ? getRequiredFields(
                                                              editableFeature,
                                                              isTreatment,
                                                              editableFeature.attributes[
                                                                  FieldNames.featureFields
                                                                      .fundingAgency
                                                              ],
                                                              true
                                                          )
                                                        : []
                                                }
                                                typeValue={typeValue}
                                                setTypeValue={setTypeValue}
                                                subTypeValue={subTypeValue}
                                                setSubTypeValue={setSubTypeValue}
                                                unitOfMeasureValue={unitOfMeasureValue}
                                                setUnitOfMeasureValue={setUnitOfMeasureValue}
                                                setCostFundingPopoverOpen={
                                                    setCostFundingPopoverOpen
                                                }
                                                setCostFundingPopoverUsePlannedActual={
                                                    setCostFundingPopoverUsePlannedActual
                                                }
                                                programValue={programValue}
                                                setProgramValue={setProgramValue}
                                                interagencySelected={interagencySelected}
                                                setInteragencySelected={setInteragencySelected}
                                                otherInteragencySelected={otherInteragencySelected}
                                                setOtherInteragencySelected={
                                                    setOtherInteragencySelected
                                                }
                                                valueReference={valueReference}
                                            />
                                        </TableCell>
                                    </>
                                )}
                            </TableRow>
                        )}
                    </Fragment>
                ))}
                {costFundingPopoverOpen && (
                    <CostsFundingPopover
                        open={costFundingPopoverOpen}
                        onClose={onCloseManageCosts}
                        editableFeature={ // TODO - branching prop!
                            costFundingPopoverUsePlannedActual ? plannedFeature : editableFeature
                        }
                        setBaseFeatureAttributes={setBaseFeatureAttributes}
                        setEditableFeature={setEditableFeature}
                        mode={costFundingPopoverUsePlannedActual ? 'read' : mode}
                        setLocationDepartmentValue={null}
                        setLocationAgencyValue={null}
                        setLocationRegionValue={null}
                        setLocationUnitValue={null}
                        featureUnsavedFields={
                            isTreatment
                                ? treatmentValidationUnsavedValues
                                : activityValidationUnsavedValues
                        }
                    />
                )}
            </Fragment>
        );
    };

    const navigateToProject = async () => {
        const entityStringValue = isFeaturePostFire(editableFeature) ? 'pf' : 'f';
        if (editableFeature?.attributes?.[FieldNames.featureFields.projectID]) {
            const projectObjectID = await ProjectService.getProjectObjectIDByID(
                editableFeature?.attributes?.[FieldNames.featureFields.projectID]
            );
            navigate(`/${entityStringValue}/projects/${projectObjectID?.attributes?.OBJECTID}`);
        }
    };

    const getProjectChip = (detail) => {
        if (detail.identifier === 'projectPanel') {
            return (
                <Fragment>
                    <Grid
                        item
                        size={12}
                        container
                        direction='row'
                        className='grid-item'
                        style={{ alignItems: 'center', maxWidth: '550px', paddingRight: '5px' }}
                    >
                        <Tooltip
                            title={
                                editableFeature?.attributes?.[FieldNames.featureFields.projectID]
                                    ? 'Return to Project'
                                    : 'There is no Project related to this action'
                            }
                        >
                            <Grid item size={3}>
                                <Chip
                                    disabled={
                                        !editableFeature?.attributes?.[
                                            FieldNames.featureFields.projectID
                                        ]
                                    }
                                    className='required-field-status-chip'
                                    onClick={() => navigateToProject()}
                                    style={{
                                        backgroundColor: `${ColorConstants.AvatarBackgroundColor}`,
                                        marginBottom: 5,
                                        marginTop: 5,
                                        zIndex: 5,
                                    }}
                                    avatar={
                                        <Avatar
                                            style={{
                                                fontSize: '12px',
                                                width: '22px',
                                                height: '22px',
                                                backgroundColor: ColorConstants.AvatarColor,
                                                color: ColorConstants.WhiteAlien,
                                            }}
                                        >
                                            <AddIcon />
                                        </Avatar>
                                    }
                                    label={
                                        <span style={{ fontSize: '12px', lineHeight: '21px' }}>
                                            Return to Project
                                        </span>
                                    }
                                />
                            </Grid>
                        </Tooltip>
                    </Grid>
                </Fragment>
            );
        } else {
            return '';
        }
    };

    const getListPanel = () => {
        let isPoint = false;
        if (
            editableFeature &&
            editableFeature.attributes &&
            editableFeature.attributes[FieldNames.featureFields.isPoint] === FieldNames.yesNo.yes
        ) {
            isPoint = true;
        }
        const isPostFire = isFeaturePostFire(editableFeature?.attributes);

        return (
            <Box
                className='incident-list-inner'
                style={{ height: '100%', overflowY: 'scroll', overflowX: 'hidden' }}
            >
                {groupDetails?.map((detail) => (
                    <div
                        className={
                            detail.identifier === 'trackingPanel'
                                ? 'incident-details-content last'
                                : 'incident-details-content'
                        }
                        key={detail.identifier}
                    >
                        {((detail?.identifier !== FieldNames.panels.project && !isPostFire) ||
                            isPostFire) &&
                            detail?.identifier !== FieldNames.panels.values && (
                                <Accordion
                                    className='incident-details-panel'
                                    expanded={
                                        expandedItems[detail.identifier] === 'true' ? true : false
                                    }
                                    defaultExpanded={true}
                                    style={{ marginTop: 10 }}
                                >
                                    <AccordionSummary
                                        id={detail.identifier}
                                        className='incident-details-summary'
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            e.preventDefault();
                                            handleExpandedChanged(
                                                e,
                                                detail.identifier,
                                                expandedItems,
                                                setExpandedItems
                                            );
                                        }}
                                        expandIcon={<ExpandMoreIcon />}
                                    >
                                        {title(detail)}
                                        {isInGroup(detail, editableFeature) &&
                                        getUnsavedFieldsCountByGroup(editableFeature, detail) > 0 &&
                                        mode === FieldNames.modes.edit ? (
                                            <DetailsEdited
                                                unsavedFields={getUnsavedFieldsCountByGroup(
                                                    editableFeature,
                                                    detail
                                                )}
                                            />
                                        ) : (
                                            ''
                                        )}
                                        {isInGroup(detail, editableFeature) &&
                                        getMissingFieldsCountByGroup(editableFeature, detail) > 0 &&
                                        mode === FieldNames.modes.edit ? (
                                            <DetailsRequired
                                                missingFields={getMissingFieldsCountByGroup(
                                                    editableFeature,
                                                    detail
                                                )}
                                            />
                                        ) : (
                                            ''
                                        )}
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        {detail.identifier === FieldNames.panels.location &&
                                            mode === FieldNames.modes.edit && (
                                                <Button
                                                    color='primary'
                                                    variant='contained'
                                                    disabled={isNewFeature}
                                                    onClick={() =>
                                                        rederiveFeatureCallback(
                                                            editableFeature,
                                                            isTreatment,
                                                            true,
                                                            isPoint,
                                                            null,
                                                            null
                                                        )
                                                    }
                                                >
                                                    Derive
                                                </Button>
                                            )}
                                        {detail.identifier === FieldNames.panels.proposal && (
                                            <Fragment>
                                                <AssociateChips
                                                    detailIdentifier={detail.identifier}
                                                    editableFeature={editableFeature}
                                                    setAssociatedCount={setAssociatedCount}
                                                    isNewFeature={isNewFeature} // NOTE: Leave unchanged at this time - prop is incorrectly renamed during drill in this instance, but correctly in another. Changing it introduces a whitescreen with untraceable bug
                                                    isLoading={isLoading}
                                                    setIsLoading={setIsLoading}
                                                    isCurrentActual={true}
                                                associatedCount={associatedCount}
                                                setEditableFeature={setEditableFeature}
                                                featureGroups={featureGroups}
                                                setFeatureGroups={setFeatureGroups}
                                            />
                                            <Chip
                                                disabled={
                                                    plannedFeature.attributes[
                                                        FieldNames.featureFields.objectID
                                                    ] === null
                                                        ? true
                                                        : false
                                                }
                                                className='required-field-status-chip'
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    e.preventDefault();
                                                    navigateToEstimated(
                                                            plannedFeature.attributes[
                                                                FieldNames.featureFields.objectID
                                                            ],
                                                            plannedFeature.attributes[
                                                                FieldNames.featureFields.entityType
                                                            ] ===
                                                                FieldNames.internalEntityTypes
                                                                    .postFire
                                                        );
                                                    }}
                                                    style={{
                                                        backgroundColor: `${ColorConstants.AvatarBackgroundColor}`,
                                                        marginBottom: 5,
                                                        marginTop: 5,
                                                        zIndex: 5,
                                                    }}
                                                    avatar={
                                                        <Avatar
                                                            style={{
                                                                fontSize: '12px',
                                                                width: '22px',
                                                                height: '22px',
                                                                backgroundColor:
                                                                    ColorConstants.AvatarColor,
                                                                color: ColorConstants.WhiteAlien,
                                                            }}
                                                        >
                                                            <SearchIcon
                                                                style={{
                                                                    width: '18px',
                                                                    height: '18px',
                                                                }}
                                                            />
                                                        </Avatar>
                                                    }
                                                    label={
                                                        <span
                                                            style={{
                                                                fontSize: '12px',
                                                                lineHeight: '21px',
                                                            }}
                                                        >
                                                            Open Estimated
                                                        </span>
                                                    }
                                                />
                                            </Fragment>
                                        )}
                                        <TableContainer style={{}}>
                                            <Table
                                                sx={{ minWidth: 500, boxShadow: 'none' }}
                                                aria-label='comparison table'
                                            >
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell
                                                            style={{
                                                                color: `${ColorConstants.Default}`,
                                                                textTransform: 'uppercase',
                                                                fontSize: '0.95rem',
                                                            }}
                                                            padding={'none'}
                                                        ></TableCell>
                                                        <TableCell
                                                            style={{
                                                                color: `${ColorConstants.Default}`,
                                                                textTransform: 'uppercase',
                                                                fontSize: '0.95rem',
                                                            }}
                                                            padding={'none'}
                                                        >
                                                            Estimated
                                                        </TableCell>
                                                        <TableCell
                                                            style={{
                                                                color: `${ColorConstants.Default}`,
                                                                textTransform: 'uppercase',
                                                                fontSize: '0.95rem',
                                                            }}
                                                            padding={'none'}
                                                        >
                                                            Actuals
                                                        </TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {tableRowUI(
                                                        detail,
                                                        editableFeature,
                                                        isTreatment
                                                    )}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                        {getProjectChip(detail)}
                                        {detail.identifier === 'trackingPanel' ? (
                                            <Box sx={{ paddingTop: '10px !important' }}>
                                                <Accordion>
                                                    <AccordionSummary
                                                        expandIcon={<ExpandMoreIcon />}
                                                        aria-controls='panel1a-content'
                                                        id='panel1a-header'
                                                    >
                                                        <Typography>View History</Typography>
                                                    </AccordionSummary>
                                                    <AccordionDetails>
                                                        <Box
                                                            sx={{
                                                                height: '250px',
                                                                width: '100%',
                                                                minWidth: '10px',
                                                            }}
                                                        >
                                                            <DataGridPremium
                                                                rows={featureHistoryRows}
                                                                columns={featureHistoryCols}
                                                            ></DataGridPremium>
                                                        </Box>
                                                    </AccordionDetails>
                                                </Accordion>
                                            </Box>
                                        ) : null}
                                    </AccordionDetails>
                                </Accordion>
                            )}
                        {detail?.identifier === FieldNames.panels.values && isPostFire && (
                            <Accordion
                                className='incident-details-panel'
                                expanded={
                                    expandedItems[detail.identifier] === 'true' ? true : false
                                }
                                defaultExpanded={true}
                                style={{ marginTop: 10 }}
                            >
                                <AccordionSummary
                                    id={detail.identifier}
                                    className='incident-details-summary'
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        handleExpandedChanged(
                                            e,
                                            detail.identifier,
                                            expandedItems,
                                            setExpandedItems
                                        );
                                    }}
                                    expandIcon={<ExpandMoreIcon />}
                                >
                                    <Typography>{detail.title}</Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <ValuesPanel
                                        joinId={
                                            editableFeature?.attributes?.[
                                                FieldNames.featureFields.joinID
                                            ]
                                        }
                                        joinFeature={
                                            editableFeature?.attributes?.[
                                                FieldNames.featureFields.joinFeature
                                            ]
                                        }
                                        setHasPostFireValues={setHasPostFireValues}
                                    />
                                </AccordionDetails>
                            </Accordion>
                        )}
                    </div>
                ))}
            </Box>
        );
    };

    return (
        <Box sx={{ display: 'flex' }}>
            <CssBaseline />
            <Drawer variant='permanent' open={true} >
                <div className='incidentList-panel'>
                    <DetailsHeader
                        editableFeature={editableFeature}
                        getAllMissingFieldsCount={getAllMissingFieldsCount}
                        getAllUnsavedFieldsCount={getAllUnsavedFieldsCount}
                        closeDetail={onClose}
                        onEditClicked={onHandleEditClicked}
                        onCopyClicked={null}
                        onCopyAsClicked={null}
                        onDeleteClicked={onHandleActualDeleteClicked}
                        onSaveChanges={onSaveChanges}
                        chipClicked={chipClicked}
                        chipClickedUnsaved={chipClickedUnsaved}
                        hasActual={false}
                        isSaveDisabled={isSaveDisabled}
                        setIsSaveDisabled={setIsSaveDisabled}
                        disableEdit={disableEdit}
                        disableEditMessage={disableEditMessage}
                        disableDelete={disableEdit}
                    />
                </div>
                <div className='incident-list' style={{ height: 'calc(100vh - 200px)' }}>
                    {getListPanel()}
                </div>
            </Drawer>
            <div style={{ top: 64 }}>
                <InFormAlert
                    openFlag={dateFYWarningOpen}
                    closeMethod={handleClose}
                    buttonMethod={handleContinue}
                    titleText={'Fiscal Year Warning'}
                    contentText={
                        'You have entered a date that is prior to the previous fiscal year, which is no longer available for data entry. Please select a date within the fiscal year(s) available for reporting.'
                    }
                />
                <ThemeProvider theme={theme}>
                    <Snackbar
                        open={snackbarOpen}
                        autoHideDuration={6000}
                        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                        className={classes.snackBarStyle}
                        onClose={() => {
                            setSnackbarOpen(false);
                            setSnackbarMessage('');
                        }}
                    >
                        <Alert
                            onClose={() => {
                                setSnackbarOpen(false);
                                setSnackbarMessage('');
                            }}
                            severity={snackbarSeverity}
                            sx={{ width: '100%' }}
                        >
                            {snackbarMessage}
                        </Alert>
                    </Snackbar>
                </ThemeProvider>
            </div>
        </Box>
    );
}
